import * as Pioneer from 'pioneer';
import { Entity, Placemarks, SceneHelpers } from 'pioneer-scripts';
import PLACEMARK_DATA from '../data/placemark_ar';
import DATA_READER_DATA from '../data/value_definitions_ar'; // importing the Arabic units
import { EVENTS_LATEST_LIMIT, POI_LATEST_LIMIT, GMAP_LATEST_LIMIT } from '../config/constants';
import { Config } from '../config/config';
import { eventsStore, poiStore, gmapStore, layersStore, lightStore, datasetStore, refsStore, spacecraftStore, videoStore } from '../managers/globalState';
import { filterEvents } from '../helpers/processEvents';
import { filterPoI } from '../helpers/processPoI';
import { filterGmap } from '../helpers/processGmap';

class SceneManager {
	/**
	 * Constructor
	 * @param {object} pioneer Pioneer engine
	 * @param {object} scene Pioneer main scene
	 * @private
	 */
	constructor(pioneer, scene, router) {
		this._pioneer = pioneer;
		this._scene = scene;
		this._router = router;

		const { get } = refsStore.stateSnapshot;
		const { missionList } = get('content');

		/**
		 * Todo: Rather than being declared in the constructor, it's
		 * cleaner to import these spacecraft arrays from a separate config - #3174.
		 */

		// console.log("missionList", missionList)
		this._allSC = missionList.map(({ external_id, categories }) => {
			if (categories?.includes('operating')) {
				const mission = get('content').getMissionId(external_id);
				return mission;
			}
			return null;
		}).filter(mission => Boolean(mission));

		if (this._allSC.includes('sc_grace_fo1')) {
			this._allSC.push('sc_grace_fo2');
		}

		if (this._allSC.includes('sc_cygnss_1')) {
			this._allSC.push('sc_cygnss_2');
			this._allSC.push('sc_cygnss_3');
			this._allSC.push('sc_cygnss_4');
			this._allSC.push('sc_cygnss_5');
			this._allSC.push('sc_cygnss_6');
			this._allSC.push('sc_cygnss_7');
			this._allSC.push('sc_cygnss_8');
		}

		this._futureSC = missionList.map(({ external_id, categories }) => {
			if (categories?.includes('planned')) {
				const mission = get('content').getMissionId(external_id);
				return mission;
			}
			return null;
		}).filter(mission => Boolean(mission));

		this._landSC = [
			'sc_grace_fo1',
			'sc_grace_fo2',
			'sc_icesat_2',
			'sc_landsat_7',
			'sc_landsat_8',
			'sc_landsat_9',
			'sc_smap',
			'sc_terra'
		];
		this._seaSC = ['sc_jason_3', 'sc_sentinel_6', 'sc_swot'];
		this._atmosphereSC = [
			'sc_aqua',
			'sc_aura',
			'sc_calipso',
			'sc_cloudsat',
			'sc_cygnss_1',
			'sc_cygnss_2',
			'sc_cygnss_3',
			'sc_cygnss_4',
			'sc_cygnss_5',
			'sc_cygnss_6',
			'sc_cygnss_7',
			'sc_cygnss_8',
			'sc_gpm',
			'sc_oco_2',
			'sc_suomi_npp',
			'sc_tropics_01',
			'sc_tropics_03',
			'sc_tropics_05',
			'sc_tropics_06',
			'sc_tropics_07'
		];

		// Options are fixed variables that should not be changed (frozen)
		this._options = Object.freeze({
			eventPlacemarks: {
				limit: Infinity,
				idPrefix: 'event_'
			},
			poiPlacemarks: {
				limit: Infinity,
				idPrefix: 'poi_'
			},
			gmapPlacemarks: {
				limit: Infinity,
				idPrefix: 'gmap_'
			}
		});

		this._eventIds = [];
		this._poiIds = [];
		this._gmapIds = [];
		this._vitalsign = '';
		this._groupId = 0;
		this._loadingItems = [];
		this._dataValueObject = null;
		this.groundTracksEntity = null;
		this.trailWidth = 2;

		// Bind handlers.
		this._onIsLoading = this._onIsLoading.bind(this);
		this._onEventsStateUpdate = this._onEventsStateUpdate.bind(this);
		this._onPoIStateUpdate = this._onPoIStateUpdate.bind(this);
		this._onGmapStateUpdate = this._onGmapStateUpdate.bind(this);
		this._onLightStateUpdate = this._onLightStateUpdate.bind(this);
		this._onCityNamesStateUpdate = this._onCityNamesStateUpdate.bind(this);
		this._onGroundTrackStateUpdate = this._onGroundTrackStateUpdate.bind(this);
		this._getLatestEventIds = this._getLatestEventIds.bind(this);

		this.displayEventPlacemarks = this.displayEventPlacemarks.bind(this);
		this.displayPoIPlacemarks = this.displayPoIPlacemarks.bind(this);
		this.displayGmapPlacemarks = this.displayGmapPlacemarks.bind(this);
		this.singleEventPlacemark = this.singleEventPlacemark.bind(this);
		this.singlePoIPlacemark = this.singlePoIPlacemark.bind(this);
		this.singleGmapPlacemark = this.singleGmapPlacemark.bind(this);
		this.showAllEventsOnGlobe = this.showAllEventsOnGlobe.bind(this);
		this.loadCanvas = this.loadCanvas.bind(this);
		this.allLoaded = this.allLoaded.bind(this);
	}

	/**
	 * Initialize the scene
	 */
	init() {
		// Create celestials
		Entity.create('observable_universe', this._scene, { skybox: false });
		Entity.create('milky_way', this._scene, { starfield: true });


		const sun = Entity.create('sun', this._scene);
		const sunSpheroid = sun.get('spheroidLOD');
		sunSpheroid.setTexture('color', sunSpheroid.getTextureUrl('color'), [4]);

		const earth = Entity.create('earth', this._scene);
		earth.get('spheroidLOD').setEnabled(false);
		const moon = Entity.create('moon', this._scene);
		const moonSpheroid = moon.get('spheroidLOD');
		moonSpheroid.setTexture('color', moonSpheroid.getTextureUrl('color'), [4, 16]);
		moonSpheroid.setTexture('normal', moonSpheroid.getTextureUrl('normal'), [4]);


		Entity.register({
			sc_saudisat_4: {
				groups: ['earth', 'spacecraft'],
				clickable: true,
				occlusionRadius: 0.00198,
				extentsRadius: 0.0015,
				label: 'Saudisat 4',
				parents: [[23030.92908476, 'earth']],
				trail: {
					length: 5653.2
				},
				model: {
					url: 'assets/ce2s2_models/SudiSatup4_12.glb',
					shadowEntities: ['earth', 'moon'],
          scale: [0.0002, 0.0002, 0.0002]
				},
				controllers: [
					{
						type: 'orbitalElements',
						name: 'sc_saudisat_4',
            eccentricity: 0.0048577,
            semiMajorAxis : 7924.41,
            meanAngularMotion: 14.7630988 * 2 * Math.PI / 3600 / 24,
            meanAnomalyAtEpoch: 255.4208 * (Math.PI / 180) ,
            inclination: 97.6455 * (Math.PI / 180),
            argumentOfPeriapsis: 105.2381 * (Math.PI / 180),
            longitudeOfAscendingNode: 178.1483 * (Math.PI / 180),
            coverage: [Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY]
					},
					{
						type: 'align',
						primary: {
							type: 'point',
							target: 'earth',
							axis: Pioneer.Vector3.YAxis
						}
					}
				]

			}
		});
//https://www.karhukoti.com/webtracker?s=40016
//https://celestrak.org/NORAD/elements/gp.php?INTDES=2014-033
//https://www.n2yo.com/satellite/?s=40016
//!!!!https://celestrak.org/NORAD/elements/gp.php?CATNR=40016&FORMAT=omm
// const epoch = 65.7327606
// const eccentricity = .0069132
// const inclination = 98.19
// const perigree = 99.5370
// const mean_anomaly = 261.3651
// const mean_motion = 14.6048579
// const rev_num_ep = 79288
// const semiMajorAxis = 7018;
// const noradPerigree = 612.9
// const noradApogee = 681.1
// const noradPeriod = 97.5 //minutes

/*
const controller = entity.addControllerByClass(Pioneer.OrbitalElementsController, controllerOptions.name);
const orbitalElements = new Pioneer.OrbitalElements();
orbitalElements.epoch = epoch;
orbitalElements.eccentricity = eccentricity;
orbitalElements.semiMajorAxis = semiMajorAxis;
orbitalElements.meanAngularMotion = meanAngularMotion;
orbitalElements.meanAnomalyAtEpoch = meanAnomalyAtEpoch;
orbitalElements.setOrbitOrientationFromElements(inclination, longitudeOfAscendingNode, argumentOfPeriapsis);
controller.addOrbitalElements(epoch, orbitalElements);
*/
		// Subscribe to global state changes.
		this._subscribeToStates();

		// Create the patch component.
		const patchComponent = earth.addComponent('spheroidLOD', 'patch');
		patchComponent.setEnabled(false);

    console.log("patchComponent in scene",  patchComponent)

		// Create the Pick controller for Earth
		earth.addController('pick', 'dataReader');

			// Setup compare objects
			const scientist = Entity.create('scientist', this._scene);
			scientist.setEnabled(false);
			const bus = Entity.create('school_bus', this._scene);
			bus.setEnabled(false);

			Entity.register(
				{
					astronaut: {
						groups: ['comparison'],
						radius: 0.000835,
						label: 'Saudi Astronaut',
						parents: [],
						model: {
							url: 'assets/ce2s2_models/astronaut/ast_without_face.glb',
							rotate: [{ x: 90 }],
							
						},
						controllers: [
							{
								type: 'fixed',
								position: Pioneer.Vector3.Zero,
								orientation: Pioneer.Quaternion.Identity
							}
						]
					}
				}
			);
			const astronaut = Entity.create('astronaut', this._scene);
			astronaut.setEnabled(false);

			Entity.register(
				{
					camel: {
						groups: ['comparison'],
						radius: 0.000835,
						label: 'Arabic Camel',
						parents: [],
						model: {
							url: 'assets/ce2s2_models/camel_May_12.glb',
							rotate: [{ x: 90 }],
							useCompressedTextures: false
						},
						controllers: [
							{
								type: 'fixed',
								position: Pioneer.Vector3.Zero,
								orientation: Pioneer.Quaternion.Identity
							}
						]
					}
				}
			);
			const Camel = Entity.create('camel', this._scene);
			Camel.setEnabled(false);

			this._allSC.forEach(sc => {
				const entity = Entity.create(sc, this._scene);
			  //  if (sc == 'sc_saudisat_4') {
				// 	entity.addComponent('gizmo');
				// }

				// for some reason code below is making our trails disappear, but is not doing that on vtad version??? -rk
        //merge questions: maybe these can be uncommented now?
				// const trailComponent = this._scene.get(sc, 'trail');
				// trailComponent?.setWidths(0, this.trailWidth);
			});
			this._futureSC.forEach(sc => Entity.create(sc, this._scene));

		// #1595 - These sc_iss lines can be removed at a later date when camera orientations for spacecraft children is implemented)
		this._scene.get('sc_iss_oco_3', 'fixed').setOrientation(new Pioneer.Quaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0));
		this._scene.get('sc_iss_oco_3', 'div').setFadeWhenCloseToCamera(false);
		this._scene.get('sc_iss_oco_3').setCanOcclude(false);

		this._scene.get('sc_iss_ecostress', 'fixed').setOrientation(new Pioneer.Quaternion(Math.sqrt(0.5), -Math.sqrt(0.5), 0, 0));
		this._scene.get('sc_iss_ecostress', 'div').setFadeWhenCloseToCamera(false);
		this._scene.get('sc_iss_ecostress').setCanOcclude(false);

		this._scene.get('sc_iss_emit', 'div').setFadeWhenCloseToCamera(false);
		this._scene.get('sc_iss_emit').setCanOcclude(false);

		// Atmosphere Missions
		this._atmosphereSC.forEach(name => {
			const trailComponent = this._scene.get(name, 'trail');
			if (trailComponent) {
				trailComponent.setColor(new Pioneer.Color(0.76, 0.44, 0.18, 0.5));
			}
		});

		// Land Missions
		this._landSC.forEach(name => {
			const trailComponent = this._scene.get(name, 'trail');
			if (trailComponent) {
				trailComponent.setColor(new Pioneer.Color(0.22, 0.64, 0.22, 0.5));
			}
		});

		// Sea Missions
		this._seaSC.forEach(name => {
			const trailComponent = this._scene.get(name, 'trail');
			if (trailComponent) {
				trailComponent.setColor(new Pioneer.Color(0.3, 0.53, 0.84, 0.5));
			}
		});
	}

	/**
	 * Returns true if everythign has finished loading. Returns false if something is still loading.
	 * @returns boolean
	 */
	allLoaded() {
		return this._loadingItems.length === 0;
	}

	addLoading(target, type) {
		const id = target + '_' + type;
		if (this._loadingItems.indexOf(id) < 0) {
			this._loadingItems.push(id);
		}

		if (this._loadingItems.length > 0) {
			const event = new CustomEvent('loading');
			window.dispatchEvent(event);
		}
	}

	removeLoading(target, type) {
		const id = target + '_' + type;
		const index = this._loadingItems.indexOf(id);
		if (index >= 0) {
			this._loadingItems.splice(index, 1);
		}

		if (this._loadingItems.length === 0) {
			const event = new CustomEvent('allloaded');
			window.dispatchEvent(event);
		}
	}

	getValueFromCanvas(x, y) {
		return this._canvasContext?.getImageData(x, y, 1, 1).data;
	}


	/**
	 * Attach callbacks to global states.
	 *
	 * Note: as the scene manager is never unmounted, we do not need to unsubscribe.
	 */
	_subscribeToStates() {
		eventsStore.subscribeAll(this._onEventsStateUpdate);
		poiStore.subscribeAll(this._onPoIStateUpdate);
		gmapStore.subscribeAll(this._onGmapStateUpdate);
		lightStore.subscribeTo('fill', this._onLightStateUpdate);
		layersStore.subscribeTo('cityNames', this._onCityNamesStateUpdate);
		layersStore.subscribeTo('groundTracks', this._onGroundTrackStateUpdate);

		datasetStore.subscribeTo('isLoading', this._onIsLoading);
	}

	/**
	 * Show/hide all events on globe.
	 * @param {boolean} showOnGlobe
	 */
	showAllEventsOnGlobe(showOnGlobe) {
		const queryObj = !showOnGlobe ?
			{ __remove: ['showOnGlobe'] } :
			{ showOnGlobe: 't' };
		const updatedQuery = this._router.updateQuery(queryObj);
		this._router.navigate(updatedQuery);
	}

	/**
	 * Show all spacecrafts in the scene.
	 */
	showAllSC() {
		this._allSC.forEach(sc => {
			try {
				this._scene.get(sc)?.setEnabled(true);

				const div = this._scene.get(sc).getComponentByType('div');
				const trail = this._scene.get(sc).getComponentByType('trail');
				if (div && trail) {
					div.setEnabled(true);
					trail.setEnabled(true);
				}
			} catch (e) {
				// just ignore this error for now
				console.error('scene_manager.showAllSC', sc, e.message);
			}
		});


		const cameraEntity = this._scene.get('camera');
		const cameraParent = cameraEntity?.getParent()?.getName();

		// hiding all the future spacecraft since they are not part of the default / current mission view
		this._futureSC.forEach(async sc => {
			const entity = this._scene.get(sc);
			if (!entity) {
				return;
			}

			if (sc === cameraParent) {
				await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set(['earth', sc]));
				await this._pioneer.waitUntilNextFrame();
				const transitionController = cameraEntity.getControllerByType('transition');
				if (transitionController) {
					await transitionController.getEndPromise();
					entity.setEnabled(false);
				}
			} else {
				entity.setEnabled(false);
			}
		});
	}

	/**
	 * Hide all spacecrafts in the scene.
	 */
	hideAllSC() {
		this._allSC.forEach(sc => {
			this._scene.get(sc).setEnabled(false);
		});
		this._futureSC.forEach(sc => {
			this._scene.get(sc).setEnabled(false);
		});
	}

	/**
	 * Show only 1 (or both GFO) spacecraft and hide the rest.
	 * Show all instruments beginning with 'sc_iss' while viewing the ISS
	 * @param {string[]} ids - Id of spacecraft
	 * @param {boolean} destroyIds - To destroy prev entities once camera finishes transitioning
	 */
	showOnlySC(ids, destroyIds = false) {
		const { get } = refsStore.stateSnapshot;

		if (!ids) {
			return;
		}

		this._allSC.forEach(sc => {
			// match all sc that begin the with the same string and show them together
			const scFound = ids.some(id => sc.includes(ids[0].split('_').slice(0, 2).join('_')) || id.startsWith(sc));
			// Ensure prev entities aren't destroyed before the camera finishes transitioning
			if (scFound || destroyIds) {
				// Enable only those that are needed, disable the rest
				this._scene.get(sc).setEnabled(scFound);
			}

			const div = this._scene.get(sc).getComponentByType('div');
			const trail = this._scene.get(sc).getComponentByType('trail');
			if (div && trail) {
				div.setEnabled(true);
				trail.setEnabled(true);
			}
		});

		this._futureSC.forEach(sc => {
			let scFound = false;
			ids.forEach(id => {
				if (sc.startsWith(id) || id.startsWith(sc)) {
					scFound = true;
				}
			});

			if (scFound || destroyIds) {
				this._scene.get(sc).setEnabled(scFound);
			}

			const div = this._scene.get(sc).getComponentByType('div');
			const trail = this._scene.get(sc).getComponentByType('trail');
			if (div && trail) {
				div.setEnabled(true);
				trail.setEnabled(true);
			}

			const entity = this._scene.get(sc);
			entity.clearParentingTableEntries();
			entity.addParentingTableEntry(Number.NEGATIVE_INFINITY, 'earth');
			const oeController = entity.getControllerByClass(Pioneer.OrbitalElementsController);

			if (oeController !== null) {
				// Assumes future s/c have orbital element controllers
				oeController.setCoverage(new Pioneer.Interval(Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY));
			} else {
				const dynamoController = entity.getControllerByClass(Pioneer.DynamoController) ?? undefined;
				const oeController = entity.addControllerByClass(Pioneer.OrbitalElementsController, undefined, dynamoController);
				const oe = new Pioneer.OrbitalElements();
				oe.epoch = this._scene.getEngine().getTime();
				// Use NISAR data for any s/c that doesn't have oeController
				oe.eccentricity = 0;
				oe.semiMajorAxis = 7118;
				oe.meanAngularMotion = 0.0011382582;
				oe.meanAnomalyAtEpoch = 0;
				oe.setOrbitOrientationFromElements(Math.PI / 180 * ((Math.random() * 180) - 90), 0, 0);
				oeController.addOrbitalElements(oe.epoch, oe);
				oeController.setCoverage(new Pioneer.Interval(Number.NEGATIVE_INFINITY, Number.POSITIVE_INFINITY));
			}
		});

		// Show GFO 1 & 2 together.
		if (ids.includes('sc_grace_fo1') || ids.includes('sc_grace_fo2')) {
			const { vitalSignId } = get('content');
			const gravity = vitalSignId === 'gravity_field_map';
			if (ids.length === 1 || gravity) {
				this._scene.get(ids[0]).getComponentByType('trail').setEnabled(true);
			}
		}

		spacecraftStore.setGlobalState({ showingAllSC: false });
	}

	/**
	 * Adds or removes the animation loading depending on global isLoading state.
	 * @param {boolean} newState
	 */
	_onIsLoading(newState) {
		const target = 'dataset';
		const type = 'animation';

		// Set the loading state if we're animating as we need to load the frames.
		if (newState === true) {
			this.addLoading(target, type);
		} else if (newState === false) {
			this.removeLoading(target, type);
		}
	}

	/**
	 * Handles a change of the light global state
	 * @param {Boolean} newState
	 */
	_onLightStateUpdate(newState) {
		if (Config.spout && Config.spoutGlobe) {
			const color = new Pioneer.Color(newState ? 1 : 0.05, newState ? 1 : 0.05, newState ? 1 : 0.05);
			this._scene.setAmbientLightColor(color);
		} else {
			const color = new Pioneer.Color(newState ? 1 : 0, newState ? 1 : 0, newState ? 1 : 0);
			for (let i = 0; i < this._pioneer.getNumViewports(); i++) {
				const lightSource = /** @type {Pioneer.LightSourceComponent} */(this._pioneer.getViewport(i).getCamera().getEntity().getComponentByType('lightSource'));
				if (lightSource !== null) {
					lightSource.setColor(color);
				}
			}
		}
	}

	/**
	 * Handles a change of the city names global state
	 * @param {Boolean} newState
	 */
	_onCityNamesStateUpdate(newState) {
		const placemarks = this._scene.get('placemarks');
		if (!placemarks || !placemarks.getNumChildren()) { return; }

		for (let i = 0; i < placemarks.getNumChildren(); i++) {
			placemarks.getChild(i).setEnabled(newState);
		}
	}

	/**
	 * Handles a change of the ground tracks global state
	 * @param {Boolean} newState
	 */
	_onGroundTrackStateUpdate(newState) {
		const { get } = refsStore.stateSnapshot;
		const { spacecraftName } = spacecraftStore.stateSnapshot;
		const { currentDataset } = datasetStore.stateSnapshot;

		if (!newState) {
			this.groundTracksEntity.getControllerByType(Pioneer.CoverageController)?.setEnabledl(false);
		}

		// Reset on change of view related to showing all the spacecraft - i.e., Satellites Now to spacecraft view
		spacecraftStore.subscribeTo('showingAllSC', () => {
			this.resetGroundTracks();
		});
		// Reset on change of individual spacecraft
		spacecraftStore.subscribeTo('spacecraftName', () => {
			this.resetGroundTracks();
		});
		// Reset on change of vital sign
		datasetStore.subscribeTo('currentDataset', () => {
			this.resetGroundTracks();
		});

		// Create child entity for spacecraft to act as ground tracks
		let hasGroundTracksEntity = false;
		let spacecraft = null;

		if (newState) {
			// If on a vital sign, use the spacecraft from mission ids
			if (currentDataset?.missionIds) {
				spacecraft = this._scene.get(currentDataset.missionIds[0]);
			} else {
				// If on spacecraft view, use spacecraft name
				spacecraft = this._scene.get(spacecraftName);
			}

			// Set ground tracks entity to be the appropriate child of the spacecraft
			for (let i = 0; i < spacecraft.getNumChildren(); i++) {
				if (spacecraft.getChild(i).getName() === `groundTracksEntity_${spacecraft.getName()}`) {
					hasGroundTracksEntity = true;
					this.groundTracksEntity = spacecraft.getChild(i);
				}
			}

			if (!hasGroundTracksEntity) {
				this.groundTracksEntity = this._scene.addEntity(`groundTracksEntity_${spacecraft.getName()}`);
			}

			// Configure ground tracks entity
			this.groundTracksEntity.setParent(this._scene.get(spacecraft.getName()));
			const fixedController = this.groundTracksEntity.addController('fixed');
			fixedController.setPosition(Pioneer.Vector3.Zero);
			fixedController.setOrientation(Pioneer.Quaternion.Identity);

			// Clamp ground tracks entity to the ground to clamp trail to ground
			const groundClampController = this.groundTracksEntity.addController('groundClamp');
			groundClampController.setDistanceFromGround(10);

			const groundTracks = this.groundTracksEntity.addComponent('trail');
			groundTracks.setColor(new Pioneer.Color(1, 1, 1, 1));
			groundTracks.setWidths(1, 3);

			// Fade trail as it reaches its endpoint
			groundTracks.setAlphaFade(0);
			groundTracks.setRelativeToEntity('earth');
			groundTracks.setRelativeToEntityOrientation(true);
			groundTracks.setIgnoreDistance(true);

			// Trails start at 8 hours behind current time (3-4 'lines' on globe), and stay on globe until 24 hours, then start to fade
			groundTracks.setRelativeStartTime(false);
			const originalStartTime = this._pioneer.getTime() - 28800;

			groundTracks.setStartTime(originalStartTime);

			const coverageController = this.groundTracksEntity.addControllerByClass(Pioneer.CoverageController);
			coverageController.setUpdateFunction(entity => {
				// Check for tracks in case something deleted them.
				const tracks = entity.getComponentByClass(Pioneer.TrailComponent);
				if (tracks === null) {
					return;
				}

				// Ground tracks are at 24 hours aka all around the globe
				if (this._pioneer.getTime() - originalStartTime >= 86400) {
					groundTracks.setStartTime(this._pioneer.getTime() - 86400);
				} else {
					// Set ground tracks back to 8 hours behind
					if (get('time').getTimeRate() < 0) {
						groundTracks.setStartTime(this._pioneer.getTime() - 28800);
					} else {
						groundTracks.setStartTime(originalStartTime);
					}
				}
			});
		}

		this.groundTracksEntity.setEnabled(newState);
	}

	resetGroundTracks() {
		// Reset toggle and disable ground tracks on globe
		layersStore.setGlobalState({ groundTracks: false });
		this.groundTracksEntity.setEnabled(false);
	}

	/**
	 * Handles a change of events global state.
	 *
	 * Events shown on the globe must always be filtered (otherwise there's too many) to prevent UI slowdown.
	 * @param {object} newState
	 */
	_onEventsStateUpdate(newState) {
		const { categoryIndex, showOnGlobe, showLatest, events, yearIndex } = newState;

		// Always turn off all events first.
		this.displayEventPlacemarks(false);

		if (showOnGlobe) {
			const { get } = refsStore.stateSnapshot;
			const { eventYears } = get('content');
			const key = eventYears[yearIndex];

			if (events[key] && Array.isArray(events[key])) {
				const data = [...events[key]];
				const filteredEvents = filterEvents(data, categoryIndex);
				const filteredEventIds = filteredEvents.map(event => event.external_id);

				let showEvenTitle = true;
				if (this._router?._history?.location?.pathname) {
					const { pathname } = this._router._history.location;
					showEvenTitle = pathname.indexOf('event') >= 0 || pathname.indexOf('spacecraft') >= 0;
				}

				 this.displayEventPlacemarks(!showEvenTitle, filteredEventIds);
			}
		} else if (showLatest) {
			const latestEventIds = this._getLatestEventIds();
			 this.displayEventPlacemarks(true, latestEventIds);
		}
	}

	/**
	 * Handles a change of poi global state.
	 *
	 * PoI shown on the globe must always be filtered (otherwise there's too many) to prevent UI slowdown.
	 * @param {object} newState
	 */
	 _onPoIStateUpdate(newState) {
		const { yearIndex, categoryIndex, showOnGlobePoI, showLatestPoI, pois, poi_cats, poiYears } = newState;
		// Always turn off all poi first.
		this.displayPoIPlacemarks(false);
		if (showOnGlobePoI) {
			const { idPrefix } = this._options.poiPlacemarks;
			const filteredPoI = filterPoI(pois, poiYears, yearIndex, categoryIndex);
			const filteredPoIIds = filteredPoI.map(poi => `poi_${poi.id}`);

			let showPoITitle = true;
			if (this._router?._history?.location?.pathname) {
				const { pathname } = this._router._history.location;
				showPoITitle = pathname.indexOf('poi') >= 0 || pathname.indexOf('spacecraft') >= 0;
			}

			this.displayPoIPlacemarks(!showPoITitle, filteredPoIIds);
		} else if (showLatestPoI) {
			const latestPoIIds = this._getLatestPoIIds();
			this.displayPoIPlacemarks(true, latestPoIIds);
		}
	}

	/**
	 * Handles a change of gmap global state.
	 *
	 * Gmap shown on the globe must always be filtered (otherwise there's too many) to prevent UI slowdown.
	 * @param {object} newState
	 */
	 _onGmapStateUpdate(newState) {
		const { yearIndex, categoryIndex, showOnGlobeGmap, showLatestGmap, gmaps, gmap_cats, gmapYears } = newState;
		// Always turn off all gmap first.
		this.displayGmapPlacemarks(false);
		if (showOnGlobeGmap) {
			const { idPrefix } = this._options.gmapPlacemarks;
			const filteredGmap = filterGmap(gmaps, gmapYears, yearIndex, categoryIndex);
			const filteredGmapIds = filteredGmap.map(gmap => `gmap_${gmap.id}`);

			let showGmapTitle = true;
			if (this._router?._history?.location?.pathname) {
				const { pathname } = this._router._history.location;
				showGmapTitle = pathname.indexOf('gmap') >= 0 || pathname.indexOf('spacecraft') >= 0;
			}

			this.displayGmapPlacemarks(!showGmapTitle, filteredGmapIds);
		} else if (showLatestGmap) {
			const latestGmapIds = this._getLatestGmapIds();
			this.displayGmapPlacemarks(true, latestGmapIds);
		}
	}

	createCityPlacemarks() {
		const earth = this._scene.get('earth');
		const placemarks = this._scene.get('placemarks');
		if (!placemarks) {
			const group = this._scene.addEntity('placemarks');
			group.setParent(earth);
			group.setCanOcclude(false);
			group.setOcclusionRadius(earth.getOcclusionRadius());
			const fixed = group.addController('fixed');
			fixed.setPosition(Pioneer.Vector3.Zero);
			fixed.setOrientation(Pioneer.Quaternion.Identity);
			group.addController('rotateByEntityOrientation');

			for (const id in PLACEMARK_DATA) {
				const data = PLACEMARK_DATA[id];
				const altitude = 10;
				const entity = Placemarks.addPlacemark(id, id, earth, data.latLng[0], data.latLng[1], altitude);
				entity.setParent(group); // Put placemark into group
				const div = entity.get('div').getDiv();
				div.id = id;
				div.classList.add('placemark');
				div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker.svg"></div>' + '<div class="content">' + data.text + '</div>';
			}
		}
	}


	singleEventPlacemark(eventId = null) {
		const { get } = refsStore.stateSnapshot;

		if (!eventId) { return; }

		if (!this._eventIds.includes(eventId)) {
			this._eventIds.push(eventId);
		}

		const earth = this._scene.get('earth');
		let group = this._scene.getEntity('events');

		if (!group) {
			group = this._scene.addEntity('events');
			group.setParent(earth);
			group.setCanOcclude(false);
			group.setExtentsRadius(earth.getExtentsRadius());
			const fixed = group.addController('fixed');
			fixed.setPosition(Pioneer.Vector3.Zero);
			fixed.setOrientation(Pioneer.Quaternion.Identity);
			group.addController('rotateByEntityOrientation');
		}

		let entity = this._scene.get(eventId);
		if (!entity) {
			const { getEventInfo } = get('content');
			const event = getEventInfo(eventId);

			if (!event || !event.center) {
				return null;
			}

			const lat = Number(event.center.split(',')[0]);
			const lon = Number(event.center.split(',')[1]);
			entity = Placemarks.addPlacemark(eventId, '', earth, lat, lon, 0, true, true);
			const entityEl = entity.get('div');

			// Put placemark into group.
			entity.setParent(group);

			const div = entityEl.getDiv();
			div.id = 'eventPlacemark_' + event.id;
			// div.classList.add('pin');
			// div.classList.add('eventPin');
			div.classList.add('placemark');
			div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker_events.svg"></div>';
			div.addEventListener('click', () => {
				eventsStore.setGlobalState({ showLatest: false });
				this._router.goToEvent(eventId);
			});
		}

		return entity;
	}

	singlePoIPlacemark(eventId = null) {
    const { get } = refsStore.stateSnapshot;

		if (!eventId) { return; }

		if (!this._poiIds.includes(eventId)) {
			this._poiIds.push(eventId);
		}

		const earth = this._scene.get('earth');
		let group = this._scene.getEntity('events');

		if (!group) {
			group = this._scene.addEntity('events');
			group.setParent(earth);
			group.setCanOcclude(false);
			group.setExtentsRadius(earth.getExtentsRadius());
			const fixed = group.addController('fixed');
			fixed.setPosition(Pioneer.Vector3.Zero);
			fixed.setOrientation(Pioneer.Quaternion.Identity);
			group.addController('rotateByEntityOrientation');
		}

		let entity = this._scene.get(eventId);

		if (!entity) {
			const { getPoIInfo } = get('content');
			const event = getPoIInfo(eventId);

			if (!event || !event.center) {
				return null;
			}

			const lat = Number(event.center.split(',')[0]);
			const lon = Number(event.center.split(',')[1]);
			entity = Placemarks.addPlacemark(eventId, '', earth, lat, lon, 0, true, true);
			const entityEl = entity.get('div');
			// Put placemark into group.
			entity.setParent(group);

			const div = entityEl.getDiv();
			div.id = 'eventPlacemark_' + event.id;
			// div.classList.add('pin');
			// div.classList.add('eventPin');
			div.classList.add('placemark');
			div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker_poi.svg"></div>';
			div.addEventListener('click', () => {
				poiStore.setGlobalState({ showOnGlobePoI: false, showLatestPoI: false });
				this._router.goToPoI(eventId);
			});
		}

		return entity;
	}

	singleGmapPlacemark(eventId = null) {
		const { get } = refsStore.stateSnapshot;
	
			if (!eventId) { return; }
	
			if (!this._gmapIds.includes(eventId)) {
				this._gmapIds.push(eventId);
			}
	
			const earth = this._scene.get('earth');
			let group = this._scene.getEntity('events');
	
			if (!group) {
				group = this._scene.addEntity('events');
				group.setParent(earth);
				group.setCanOcclude(false);
				group.setExtentsRadius(earth.getExtentsRadius());
				const fixed = group.addController('fixed');
				fixed.setPosition(Pioneer.Vector3.Zero);
				fixed.setOrientation(Pioneer.Quaternion.Identity);
				group.addController('rotateByEntityOrientation');
			}
	
			let entity = this._scene.get(eventId);
	
			if (!entity) {
				const { getGmapInfo } = get('content');
				const event = getGmapInfo(eventId);
	
				if (!event || !event.center) {
					return null;
				}
	
				const lat = Number(event.center.split(',')[0]);
				const lon = Number(event.center.split(',')[1]);
				entity = Placemarks.addPlacemark(eventId, '', earth, lat, lon, 0, true, true);
				const entityEl = entity.get('div');
				// Put placemark into group.
				entity.setParent(group);
	
				const div = entityEl.getDiv();
				div.id = 'eventPlacemark_' + event.id;
				// div.classList.add('pin');
				// div.classList.add('eventPin');
				div.classList.add('placemark');
				// div.innerHTML = '<div class="pin"><img class="no-drag" src="assets/images/map_marker_gmap.svg"></div>';
				div.addEventListener('click', () => {
					gmapStore.setGlobalState({ showOnGlobeGmap: false, showLatestGmap: false });
					this._router.goToGmap(eventId);
				});
			}
	
			return entity;
		}

	/**
	 * Show / Hide event placemarks.
	 *
	 * @param {boolean} value
	 * @param {Array} eventIds - optional list (otherwise all event placemarks)
	 *
	 * Problems:
	 * As noted in the camera_manger in the goToDataPatch, having to enable / disable all individual
	 * entities and their elements is inefficient.
	 * To turn on an off a group of HTML elements would be more efficient if they had a common parent element.
	 * We could then simply add and remove the hidden class once, rather than for each indiviudal element.
	 */
	displayEventPlacemarks(value, eventIds = this._eventIds) {
		if (!eventIds) { return; }

		const cameraEntity = this._scene.get('camera');
		const cameraParent = cameraEntity?.getParent()?.getName();

		const handleEntity = (entity, value) => {
			if (!entity) {
				return;
			}

			const entityEl = entity.get('div');
			entity.setEnabled(value);
			entityEl.setEnabled(value);
		};

		eventIds.forEach(async eventId => {
			const entity = this.singleEventPlacemark(eventId);

			if (eventId === cameraParent) {
				await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set(['earth', eventId]));

				const transitionController = cameraEntity.getControllerByType('transition');
				if (transitionController) {
					await transitionController.getEndPromise();
					handleEntity(entity, value);
				}
			} else if (entity) {
				handleEntity(entity, value);
			}
		});
	}


	displayPoIPlacemarks(value, eventIds = this._poiIds) {
		if (!eventIds) { return; }

		const cameraEntity = this._scene.get('camera');
		const cameraParent = cameraEntity?.getParent()?.getName();

		const handleEntity = (entity, value) => {
			if (!entity) {
				return;
			}

			const entityEl = entity.get('div');
			entity.setEnabled(value);
			entityEl.setEnabled(value);
		};

		eventIds.forEach(async eventId => {
			const entity = this.singlePoIPlacemark(eventId);

			if (eventId === cameraParent) {
				await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set(['earth', eventId]));

				const transitionController = cameraEntity.getControllerByType('transition');
				if (transitionController) {
					await transitionController.getEndPromise();
					handleEntity(entity, value);
				}
			} else if (entity) {
				handleEntity(entity, value);
			}
		});
	}

	displayGmapPlacemarks(value, eventIds = this._gmapIds) {
		if (!eventIds) { return; }

		const cameraEntity = this._scene.get('camera');
		const cameraParent = cameraEntity?.getParent()?.getName();

		const handleEntity = (entity, value) => {
			if (!entity) {
				return;
			}

			const entityEl = entity.get('div');
			entity.setEnabled(value);
			entityEl.setEnabled(value);
		};

		eventIds.forEach(async eventId => {
			const entity = this.singleGmapPlacemark(eventId);

			if (eventId === cameraParent) {
				await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set(['earth', eventId]));

				const transitionController = cameraEntity.getControllerByType('transition');
				if (transitionController) {
					await transitionController.getEndPromise();
					handleEntity(entity, value);
				}
			} else if (entity) {
				handleEntity(entity, value);
			}
		});
	}

	/**
	 * Enables the most recent event placemarks.
	 * @param {number} number
	 */
	_getLatestEventIds(limit = EVENTS_LATEST_LIMIT) {
		const { get } = refsStore.stateSnapshot;
		const { latestEvents } = get('content');

		return latestEvents.filter((event, i) => i < limit).map(event => event.external_id);
	}

	/**
	 * Enables the most recent poi placemarks.
	 * @param {number} number
	 */
	_getLatestPoIIds(limit = POI_LATEST_LIMIT) {
		const { pois } = poiStore.stateSnapshot;
		const { idPrefix } = this._options.poiPlacemarks;

		return pois
			.filter((poi, i) => i < limit)
			.map(poi => `${idPrefix}${poi.id}`);
	}

	/**
	 * Enables the most recent gmap placemarks.
	 * @param {number} number
	 */
	 _getLatestGmapIds(limit = GMAP_LATEST_LIMIT) {
		const { gmaps } = gmapStore.stateSnapshot;
		const { idPrefix } = this._options.gmapPlacemarks;

		return gmaps
			.filter((gmap, i) => i < limit)
			.map(gmap => `${idPrefix}${gmap.id}`);
	}

	async loadVitalSign(vitalsign, { mission, currentDataset, groupId = 0 } = {}) {
		const { get } = refsStore.stateSnapshot;

		const { isSatellitesNow } = get('content');
		const { dataset, isVisibleEarth } = get('dataset');
		const { latestTileset } = dataset || {};

		// enable wmts for satellites
		if (isVisibleEarth || isSatellitesNow) {
			this.showAllSC();
			spacecraftStore.setGlobalState({ showingAllSC: true });
		} else if (latestTileset) {
			const { missionIds } = dataset;
			this.showOnlySC([...missionIds]);
			spacecraftStore.setGlobalState({ showingAllSC: false });


			const file = get('dataset').getTilesetPath();
			this.loadCanvas(file);
			await this.toggleTexture('dataset', { file });
		}
	}

	// TODO: move this to the data_readout component. - #3186.
	loadCanvas(file) {
		// This section loads the data image into a canvas for data lookup.
		const dataImage = new Image();
		dataImage.onload = () => {
			const canvas = document.getElementById('data-canvas');
			this._canvasContext = canvas?.getContext('2d', { willReadFrequently: true });
			this._canvasContext?.drawImage(dataImage, 0, 304, 1440, 720, 0, 0, 1440, 720);
		};
		dataImage.crossOrigin = 'Anonymous'; // Work around crossOrigin error.
		dataImage.src = file + '6.png';
	}

	async toggleTexture(type, { file = '' } = {}) {
		const { get } = refsStore.stateSnapshot;
		const earth = this._scene.get('earth');
		const spheroid = earth.get('spheroidLOD');
		const atmosphere = earth.get('atmosphere');

		const textureNames = ['specular', 'decal', 'normal', 'night'];
		const materials = spheroid.getThreeJsMaterials();

		layersStore.setGlobalState({ blueMarble: type === 'bluemarble' });

		switch (type) {
			case 'wmts':
				get('visibleEarth').init();
				break;
			case 'dataset':
				// default Explorer cube layout
				spheroid.setCubeMapLayout([
					['+z', '+x', '+y'],
					['+z', '+x', '-y'],
					['-y', '+x', '-z'],
					['-y', '+x', '+z'],
					['-y', '+z', '-x'],
					['-y', '+z', '+x']
				]);
				spheroid.setMapping('cube');
				spheroid.setTexture('color', file + '$FACE.png');

				/**
				 * Unset textures.
				 * Not sure what changed in the spheroidLOD that makes the old code not work
				 * or whether this is necessary anymore but this is a safe way to try.
				 */
				textureNames.forEach(textureName => {
					materials.forEach(material => {
						if (material.uniforms[`${textureName}Texture`]?.value) {
							spheroid.unsetTexture(textureName);
						}
					});
				});

				spheroid.setFeature('normalMap', false);
				spheroid.setFeature('nightMap', false);
				spheroid.setFeature('decalMap', false);
				spheroid.setFeature('specularMap', false);

				await this._pioneer.waitUntilNextFrame();
				await spheroid.getLoadedPromise();

				spheroid.setEnabled(true);
				atmosphere.setEnabled(false);

				// TODO: Add callback for data reading to earth.pickController
				break;
			case 'bluemarble':
			default: // Blue Marble
				get('visibleEarth').hide(true);
				// default Pioneer cube layout
				spheroid.setCubeMapLayout([
					['+y', '+z', '+x'],
					['-x', '+z', '+y'],
					['-y', '+z', '-x'],
					['+x', '+z', '-y'],
					['+y', '-x', '+z'],
					['+y', '+x', '-z']
				]);

				spheroid.setMapping('cube');
				spheroid.setTexture('color', '$STATIC_ASSETS_URL/maps/earth/color_$SIZE_$FACE.png', [16, 512, 2048, 4096]);
				spheroid.setTexture('specular', '$STATIC_ASSETS_URL/maps/earth/color_$SIZE_$FACE.png', [16, 512, 2048, 4096]);
				spheroid.setTexture('normal', '$STATIC_ASSETS_URL/maps/earth/normal_$SIZE_$FACE.png', [16, 512, 2048, 4096]);
				spheroid.setTexture('night', '$STATIC_ASSETS_URL/maps/earth/night_$SIZE_$FACE.png', [16, 512, 2048, 4096]);
				spheroid.setFeature('normalMap', true);
				spheroid.setFeature('nightMap', true);
				spheroid.setFeature('specularMap', true);

				// disable clouds
				spheroid.setFeature('decalMap', false);

				await this._pioneer.waitUntilNextFrame();
				await spheroid.getLoadedPromise();

				spheroid.setEnabled(true);
				atmosphere.setEnabled(false);
				break;
		}
	}
}

export default SceneManager;
